import { useState } from 'react';
import { Alert, Button, Chip, IconButton, Stack, Switch } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { PageHeader, DataTable, ConfirmDialog, AdminPageState } from '@shared/components';
import { useApiQuery } from '@shared/hooks/useApi.js';
import { api } from '@core/api.js';
import { useNotify } from '@shared/hooks/useNotify.js';
import { formatDateTime } from '@shared/utils/formatters.js';
import { apiErrorMessage } from '@shared/utils/apiErrorMessage.js';
import ApiKeyFormDialog from '../components/ApiKeyFormDialog.jsx';

export default function ApiKeysPage() {
  const notify = useNotify();
  const {
    data: keys = [], isLoading, isError, error, refetch,
  } = useApiQuery('api-keys', '/v1/api-keys');
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [newToken, setNewToken] = useState(null);

  const toggle = async (key) => {
    if (!key?.id) {
      notify.error('Identifiant de clé invalide.');
      return;
    }
    try { await api.patch(`/v1/api-keys/${key.id}`, { active: !key.active }); refetch(); }
    catch (err) { notify.error(apiErrorMessage(err, 'Échec de mise à jour de la clé.')); }
  };

  const remove = async () => {
    if (!confirming?.id) {
      notify.error('Identifiant de clé invalide.');
      setConfirming(null);
      return;
    }
    setRevoking(true);
    try {
      await api.delete(`/v1/api-keys/${confirming.id}`);
      notify.success('Révoquée');
      refetch();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Échec de révocation de la clé.'));
    } finally {
      setRevoking(false);
      setConfirming(null);
    }
  };

  const copy = (value) => {
    navigator.clipboard?.writeText(value).then(() => notify.success('Copié'));
  };

  const columns = [
    { field: 'name', headerName: 'Nom', flex: 1 },
    { field: 'prefix', headerName: 'Préfixe', width: 120, renderCell: (params) => <code>{params?.value ?? '—'}…</code> },
    { field: 'scopes', headerName: 'Scopes', flex: 1, renderCell: (params) => (
      <Stack direction="row" spacing={0.5}>{(params?.value || []).map((s) => <Chip key={s} size="small" label={s} />)}</Stack>
    ) },
    { field: 'rateLimit', headerName: 'Rate', width: 90 },
    { field: 'usageCount', headerName: 'Appels', width: 100 },
    { field: 'lastUsedAt', headerName: 'Dernier appel', width: 180, valueFormatter: (value) => value ? formatDateTime(value) : '—' },
    { field: 'active', headerName: 'Active', width: 90, renderCell: (params) => <Switch size="small" checked={Boolean(params?.row?.active)} onChange={() => params?.row && toggle(params.row)} /> },
    { field: 'actions', headerName: '', width: 80, sortable: false, renderCell: (params) => (
      <IconButton size="small" color="error" onClick={() => params?.row && setConfirming(params.row)}><DeleteIcon fontSize="small" /></IconButton>
    ) },
  ];

  return (
    <>
      <PageHeader
        dense
        title="Clés API"
        subtitle="Authentification des appels /api/public/*. Les leads publics sont créés dans le workspace de la clé (doit correspondre à celui de l’admin)."
        actions={<Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}>Nouvelle clé</Button>}
      />
      {newToken && (
        <Alert severity="success" onClose={() => setNewToken(null)} sx={{ mb: 2 }}
          action={<IconButton size="small" onClick={() => copy(newToken)}><ContentCopyIcon fontSize="small" /></IconButton>}
        >
          Votre clé : <code>{newToken}</code>. Conservez-la, elle ne sera plus affichée.
        </Alert>
      )}
      <AdminPageState
        isLoading={isLoading}
        isError={isError}
        error={error}
        onRetry={refetch}
        isEmpty={keys.length === 0}
        emptyTitle="Aucune clé"
        emptyDescription="Créez une clé pour exposer votre API publique."
      >
        <DataTable rows={keys} columns={columns} loading={isLoading} density="compact" />
      </AdminPageState>
      {creating && (
        <ApiKeyFormDialog
          onClose={(token) => { setCreating(false); if (token) setNewToken(token); refetch(); }}
        />
      )}
      <ConfirmDialog
        open={!!confirming}
        title="Révoquer"
        message={`Révoquer la clé "${confirming?.name}" ?`}
        destructive
        loading={revoking}
        loadingLabel="Révocation…"
        onCancel={() => setConfirming(null)}
        onConfirm={remove}
      />
    </>
  );
}
